import { Component, CSSProperties } from 'react';
import MonacoEditor from 'react-monaco-editor';
import * as Message from '../common/js/message';
import { Request } from '../common/js/request';
import { Context, AppContext } from "../common/js/context";
import { T, COMMON } from '../common/js/i18n';
import { renderCode, exportTemplate } from "./common";
import ModalDialog from "../components/ModalDialog";
import iconXmark from '../assets/icon/xmark.svg';
import iconPenToSquare from '../assets/icon/pen-to-square.svg';
import iconCircleQuestion from '../assets/icon/circle-question.svg';
import iconMaximize from '../assets/icon/maximize.svg';
import iconMinimize from '../assets/icon/minimize.svg';
import styles from './Edit.module.css';
import DataProcessorEdit from "./DataProcessorEdit";
import DataHelp from "./help/DataHelp";
import TemplateHelp from "./help/TemplateHelp";
import ProcessorHelp from "./help/ProcessorHelp";
import ClientDownload from "./help/ClientDownload";

enum CodeType {
    Data = "Data",
    Template = "Template",
}

interface TemplateDetail {
    id: string;
    name: string;
    content: string;
    export_path?: string;
    created_time: string;
    last_modified_time: string;
}

interface SchemeDetail {
    id: string;
    name: string;
    demo_data: string;
    data_processor?: string;
    created_time: string;
    last_modified_time: string;
    templates: TemplateDetail[];
}

interface Props {
    schemeId: string;
}

interface Template {
    id?: string;
    name: string;
    content: string;
    export_path?: string;
}

interface TemplateWrapper {
    template: Template;
    editing: boolean;
}

interface State {
    codeType?: CodeType;
    data: string;
    demo_data: string;
    data_processor?: string;
    templates: TemplateWrapper[];
    activeTemplateIndex: number;
    hoverIndex: number;
    code: string;
    demoDataEditActive: boolean;
    processorEditActive: boolean;
    dataHelpActive: boolean;
    templateHelpActive: boolean;
    processorHelpActive: boolean;
    downloadActive: boolean;
}

export default class Edit extends Component<Props, State> {
    static contextType = Context;
    constructor(props: Props, context: AppContext) {
        super(props);
        this.state = {
            codeType: undefined,
            data: "{}",
            demo_data: "{}",
            data_processor: "",
            templates: [{
                editing: false,
                template: {
                    name: { en: "Demo scheme", zh: "Demo方案" }[context.lang],
                    content: "",
                    export_path: undefined,
                }
            }],
            activeTemplateIndex: 0,
            hoverIndex: -1,
            code: "",
            demoDataEditActive: false,
            processorEditActive: false,
            dataHelpActive: false,
            templateHelpActive: false,
            processorHelpActive: false,
            downloadActive: false,
        };
    }
    componentDidMount() {
        this.readDetail(this.props.schemeId);
    }
    componentWillReceiveProps(nextProps: Props) {
        if (this.props.schemeId !== nextProps.schemeId) {
            this.readDetail(nextProps.schemeId);
        }
    }
    async readDetail(schemeId: string) {
        const context: AppContext = this.context as any;
        let scheme: SchemeDetail = await new Request<{
            id: string;
        }, SchemeDetail>("/api/scheme/readScheme").call({
            id: schemeId
        });
        let templates: TemplateWrapper[] = scheme.templates.map((item) => {
            return {
                editing: false,
                template: {
                    id: item.id,
                    name: item.name,
                    content: item.content,
                    export_path: item.export_path,
                }
            }
        });
        if (!templates.length) {
            templates = [{
                editing: false,
                template: {
                    name: { en: "Demo scheme", zh: "Demo方案" }[context.lang],
                    content: "",
                    export_path: undefined
                }
            }];
        }
        this.setState({
            data: scheme.demo_data,
            demo_data: scheme.demo_data,
            data_processor: scheme.data_processor,
            templates: templates,
            activeTemplateIndex: 0
        });
        renderCode(templates[0].template.content, scheme.demo_data, (code) => {
            this.setState({
                code: code
            });
        }, scheme.data_processor);
    }
    cloneTemplates(): TemplateWrapper[] {
        return this.state.templates.map(({ editing, template }) => {
            return {
                editing: editing,
                template: {
                    id: template.id,
                    name: template.name,
                    content: template.content,
                    export_path: template.export_path,
                }
            }
        });
    }
    async updateTemplate() {
        const context: AppContext = this.context as any;
        await new Request("/api/scheme/updateTemplate").call({
            id: this.props.schemeId,
            demo_data: this.state.demo_data,
            data_processor: this.state.data_processor,
            templates: this.state.templates.map(({ template }) => {
                return {
                    id: template.id,
                    name: template.name,
                    content: template.content,
                    export_path: template.export_path,
                }
            }),
        });
        Message.success(COMMON.SAVE_SUCCESSFULLY[context.lang]);
    }
    async exportTemplate() {
        const context: AppContext = this.context as any;
        if (!(window as any).__TAURI__) {
            this.setState({
                downloadActive: true
            });
            return;
        }
        let data = this.state.data;
        let data_processor = this.state.data_processor;
        let templates = [];
        for (let templateWrapper of this.state.templates) {
            let content = templateWrapper.template.content;
            let export_path_template = templateWrapper.template.export_path;
            let export_path: string | undefined = undefined;
            if (export_path_template) {
                let template: string = export_path_template;
                export_path = await new Promise((resolve, reject) => {
                    renderCode(template, data, resolve, data_processor);
                });
            }
            let code: string = await new Promise((resolve, reject) => {
                renderCode(content, data, resolve, data_processor);
            });
            templates.push({
                code: code,
                export_path: export_path
            });
        }
        let done = await exportTemplate(templates);
        if (done) {
            Message.success({ en: "Export successfully", zh: "导出成功" }[context.lang]);
        }
    }
    switchToEditable(index: number, evt: any) {
        let templates = this.cloneTemplates();
        templates[index].editing = true;
        this.setState({
            templates: templates
        });
        evt.stopPropagation();
    }

    removeTemplate(index: number, evt: any) {
        let templates = this.cloneTemplates();
        templates.splice(index, 1);
        let activeTemplateIndex = this.state.activeTemplateIndex;
        if (templates.length <= activeTemplateIndex) {
            activeTemplateIndex = templates.length - 1;
        }
        this.setState({
            templates: templates,
            activeTemplateIndex: activeTemplateIndex
        });
        renderCode(templates[activeTemplateIndex].template.content, this.state.data, (code) => {
            this.setState({
                code: code
            });
        }, this.state.data_processor);
        evt.stopPropagation();
    }

    render() {
        const options = {
            selectOnLineNumbers: true
        };
        let titleStyle: CSSProperties = { marginTop: "0.5em", marginBottom: "0.5em" };
        let panelStyle: CSSProperties = { height: "100%", display: "flex", flexDirection: "column" };
        let containerStyle: CSSProperties = { flex: "1" };
        const renderDataEditor = () => {
            let resizeListener: any = undefined;
            return (
                <div style={panelStyle}>
                    <h4 style={titleStyle}>
                        {T({ en: "Data", zh: "数据" })}:
                        <img src={iconCircleQuestion} alt="" onClick={() => {
                            this.setState({
                                dataHelpActive: true
                            });
                        }} className={styles['action-icon']} style={{ marginLeft: "0.5em" }} />
                        <div style={{ float: "right" }}>
                            {
                                [
                                    <button type="button" className="btn" onClick={() => {
                                        this.setState({
                                            demoDataEditActive: true
                                        });
                                    }}>{T({ en: "Demo data", zh: "demo数据" })}</button>,
                                    <button type="button" className="btn" onClick={() => {
                                        this.setState({
                                            processorEditActive: true
                                        });
                                    }}>{T({ en: "Data pre-processor", zh: "数据预处理器" })}</button>,
                                    <img src={iconCircleQuestion} alt="" onClick={() => {
                                        this.setState({
                                            processorHelpActive: true
                                        });
                                    }} className={styles['action-icon']} style={{ marginLeft: "0.25em", marginRight: '1em' }} />
                                ]
                            }
                            {
                                CodeType.Data === this.state.codeType &&
                                <img src={iconMinimize} alt="" onClick={() => {
                                    this.setState({
                                        codeType: undefined
                                    });
                                }} className={styles['action-icon']} />
                            }
                            {
                                !this.state.codeType &&
                                <img src={iconMaximize} alt="" onClick={() => {
                                    this.setState({
                                        codeType: CodeType.Data
                                    });
                                }} className={styles['action-icon']} />
                            }
                        </div>
                    </h4>
                    <div style={containerStyle}>
                        <MonacoEditor
                            width="100%"
                            height="100%"
                            language="json"
                            theme="vs-dark"
                            value={this.state.data}
                            options={options}
                            onChange={(newVal) => {
                                this.setState({
                                    data: newVal
                                });
                                renderCode(this.state.templates[this.state.activeTemplateIndex].template.content, newVal, (code) => {
                                    this.setState({
                                        code: code
                                    });
                                }, this.state.data_processor);
                            }}
                            editorDidMount={(editor, monaco) => {
                                if (!resizeListener) {
                                    resizeListener = () => {
                                        editor.layout();
                                    };
                                    window.addEventListener("resize", resizeListener);
                                }
                            }}
                            editorWillUnmount={() => {
                                if (resizeListener) {
                                    window.removeEventListener("resize", resizeListener);
                                    resizeListener = undefined;
                                }
                            }}
                        />
                    </div>
                </div>
            );
        };

        const renderTemplateEditor = () => {
            let resizeListener: any = undefined;
            return (
                <div style={panelStyle}>
                    <h4 style={titleStyle}>
                        {T({ en: "Template", zh: "模板" })}:
                        <img src={iconCircleQuestion} alt="" onClick={() => {
                            this.setState({
                                templateHelpActive: true
                            });
                        }} className={styles['action-icon']} style={{ marginLeft: "0.5em" }} />
                        <span style={{ marginLeft: "1em" }}>{T({ en: "Relative path while exporting", zh: "导出相对路径" })}:</span>
                        <input type="text" value={this.state.templates[this.state.activeTemplateIndex].template.export_path || ""} onInput={(evt) => {
                            let templates = this.cloneTemplates();
                            templates[this.state.activeTemplateIndex].template.export_path = (evt.target as any).value || undefined;
                            this.setState({
                                templates: templates
                            });
                        }} style={{ marginLeft: "0.5em" }} />
                        <span style={{ marginLeft: "0.5em", fontWeight: "normal" }}>{T({ en: "Template syntax supported", zh: "支持模板语法" })}</span>
                        {
                            CodeType.Template === this.state.codeType &&
                            <img src={iconMinimize} alt="" onClick={() => {
                                this.setState({
                                    codeType: undefined
                                });
                            }} className={styles['action-icon']} style={{ float: "right" }} />
                        }
                        {
                            !this.state.codeType &&
                            <img src={iconMaximize} alt="" onClick={() => {
                                this.setState({
                                    codeType: CodeType.Template
                                });
                            }} className={styles['action-icon']} style={{ float: "right" }} />
                        }
                    </h4>
                    <div style={containerStyle}>
                        <MonacoEditor
                            width="100%"
                            height="100%"
                            language="lua"
                            theme="vs-dark"
                            value={this.state.templates[this.state.activeTemplateIndex].template.content}
                            options={options}
                            onChange={(newVal: any, e: any) => {
                                let templates = this.cloneTemplates();
                                templates[this.state.activeTemplateIndex].template.content = newVal;
                                this.setState({
                                    templates: templates
                                });
                                renderCode(newVal, this.state.data, (code) => {
                                    this.setState({
                                        code: code
                                    });
                                }, this.state.data_processor);
                            }}
                            editorDidMount={(editor, monaco) => {
                                if (!resizeListener) {
                                    resizeListener = () => {
                                        editor.layout();
                                    };
                                    window.addEventListener("resize", resizeListener);
                                }
                            }}
                            editorWillUnmount={() => {
                                if (resizeListener) {
                                    window.removeEventListener("resize", resizeListener);
                                    resizeListener = undefined;
                                }
                            }}
                        />
                    </div>
                </div>
            );
        };

        const renderTemplateTab = (item: TemplateWrapper, index: number) => {
            let active = this.state.activeTemplateIndex === index;
            let iconsActive = index === this.state.hoverIndex && index === this.state.activeTemplateIndex;
            let style: CSSProperties = {
                cursor: "pointer"
            };
            if (item.editing) {
                style.padding = "0.25em 0";
            } else {
                if (iconsActive) {
                    style.padding = "0.25em 3em 0.25em 0";
                } else {
                    style.padding = "0.25em 1.5em";
                }
            }
            if (active) {
                style.borderBottom = "2px solid rgb(25, 118, 210)";
                style.cursor = "default";
            }
            return (
                <li key={index} style={{ display: "inline-block", position: "relative", ...style }} onClick={() => {
                    this.setState({
                        activeTemplateIndex: index
                    });
                    renderCode(item.template.content, this.state.data, (code) => {
                        this.setState({
                            code: code
                        });
                    }, this.state.data_processor);
                }} onMouseEnter={() => {
                    this.setState({
                        hoverIndex: index
                    });
                }} onMouseLeave={() => {
                    this.setState({
                        hoverIndex: -1
                    });
                }}>
                    {
                        item.editing && [
                            <input type="text" style={{ width: "8em" }} value={item.template.name} onInput={(evt) => {
                                let templates = this.cloneTemplates();
                                templates[index].template.name = (evt.target as any).value;
                                this.setState({
                                    templates: templates
                                });
                            }} />,
                            <button type="button" className="btn" onClick={(evt) => {
                                let templates = this.cloneTemplates();
                                templates[index].editing = false;
                                this.setState({
                                    templates: templates
                                });
                                evt.stopPropagation();
                            }}>{T(COMMON.OK)}</button>
                        ]
                    }
                    {
                        !item.editing && [
                            item.template.name,
                            iconsActive && <span style={{ position: "absolute", right: 0 }}>
                                <img src={iconPenToSquare} alt="" className={styles['action-icon']} onClick={this.switchToEditable.bind(this, index)} />
                                {
                                    1 < this.state.templates.length && <img src={iconXmark} alt="" className={styles['action-icon']} style={{ marginLeft: "0.25em" }} onClick={this.removeTemplate.bind(this, index)} />
                                }
                            </span>
                        ]
                    }
                </li>
            )
        };

        let resizeListener: any = undefined;
        const context: AppContext = this.context as any;
        return (
            <div style={{ overflow: "auto", width: "100%", height: "100%" }}>
                {
                    CodeType.Data === this.state.codeType && renderDataEditor()
                }
                {
                    CodeType.Template === this.state.codeType && renderTemplateEditor()
                }
                {
                    !this.state.codeType &&
                    <>
                        <div style={{ width: "50%", height: "100%", display: "inline-block", boxSizing: "border-box", paddingRight: "0.5em", float: "left" }}>
                            <div style={{ height: "30%" }}>
                                {
                                    renderDataEditor()
                                }
                            </div>
                            <div style={{ height: "70%", display: "flex", flexDirection: "column" }}>
                                <div style={{ flexGrow: 0, display: "flex", justifyContent: "space-between" }}>
                                    <ul className={styles['tmpl-tab-ul']}>
                                        {
                                            this.state.templates.map(renderTemplateTab)
                                        }
                                        <li style={{ display: "inline-block", padding: "0.25em 0 0.25em 1em" }}>
                                            <button type="button" className="btn" onClick={() => {
                                                let defaultContent = "";
                                                let templates = this.state.templates.concat([{
                                                    editing: false,
                                                    template: {
                                                        name: { en: "New template", zh: "新模板" }[context.lang],
                                                        content: defaultContent,
                                                        export_path: undefined,
                                                    }
                                                }]);
                                                let lastIndex = templates.length - 1;
                                                this.setState({
                                                    templates: templates,
                                                    activeTemplateIndex: lastIndex
                                                });
                                                renderCode(defaultContent, this.state.data, (code) => {
                                                    this.setState({
                                                        code: code
                                                    });
                                                }, this.state.data_processor);
                                            }}>{T(COMMON.ADD)}</button>
                                        </li>
                                    </ul>
                                    <div style={{ flexGrow: 0, display: "flex", alignItems: "center" }}>
                                        <button type="button" className="btn" onClick={this.updateTemplate.bind(this)}>{T(COMMON.SAVE)}</button>
                                    </div>
                                </div>
                                <div style={{ flexGrow: 1 }}>
                                    {
                                        renderTemplateEditor()
                                    }
                                </div>
                            </div>
                        </div>
                        <div style={{ width: "50%", height: "100%", display: "inline-block", boxSizing: "border-box", paddingLeft: "0.5em", float: "right" }}>
                            <div style={panelStyle}>
                                <h4 style={titleStyle}>
                                    {T({ en: "Code", zh: "代码" })}:
                                    <button type="button" className="btn" onClick={this.exportTemplate.bind(this)} style={{ float: "right", marginRight: "0.5em" }}>{T({ en: "Export code", zh: "导出代码" })}</button>
                                </h4>
                                <div style={containerStyle}>
                                    <MonacoEditor
                                        width="100%"
                                        height="100%"
                                        language="javascript"
                                        theme="vs-dark"
                                        value={this.state.code}
                                        options={{
                                            selectOnLineNumbers: true,
                                            readOnly: true
                                        }}
                                        editorDidMount={(editor, monaco) => {
                                            if (!resizeListener) {
                                                resizeListener = () => {
                                                    editor.layout();
                                                };
                                                window.addEventListener("resize", resizeListener);
                                            }
                                        }}
                                        editorWillUnmount={() => {
                                            if (resizeListener) {
                                                window.removeEventListener("resize", resizeListener);
                                                resizeListener = undefined;
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                }
                {
                    this.state.demoDataEditActive && (
                        <ModalDialog title={{ en: "Modify demo data", zh: "编辑demo数据" }[context.lang]} closable={false} centerStyle={{
                            width: '80%',
                            height: '80%',
                        }} dialogStyle={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column'
                        }} contentStyle={{
                            flex: 1,
                        }}>
                            <DataProcessorEdit value={this.state.demo_data} onDone={(value) => {
                                this.setState({
                                    demo_data: value,
                                    demoDataEditActive: false
                                });
                            }} onClose={() => {
                                this.setState({
                                    demoDataEditActive: false
                                });
                            }} />
                        </ModalDialog>
                    )
                }
                {
                    this.state.processorEditActive && (
                        <ModalDialog title={{ en: "Modify data pre-processor", zh: "编辑数据预处理器" }[context.lang]} closable={false} centerStyle={{
                            width: '80%',
                            height: '80%',
                        }} dialogStyle={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column'
                        }} contentStyle={{
                            flex: 1,
                        }}>
                            <DataProcessorEdit value={this.state.data_processor || ''} onDone={(value) => {
                                this.setState({
                                    data_processor: value,
                                    processorEditActive: false
                                });
                                renderCode(this.state.templates[this.state.activeTemplateIndex].template.content, this.state.data, (code) => {
                                    this.setState({
                                        code: code
                                    });
                                }, value);
                            }} onClose={() => {
                                this.setState({
                                    processorEditActive: false
                                });
                            }} />
                        </ModalDialog>
                    )
                }
                {
                    this.state.dataHelpActive && (
                        <ModalDialog title={{ en: "Data writing assistance", zh: "数据编辑帮助" }[context.lang]} closable={false}>
                            <DataHelp onClose={() => {
                                this.setState({
                                    dataHelpActive: false
                                });
                            }} />
                        </ModalDialog>
                    )
                }
                {
                    this.state.templateHelpActive && (
                        <ModalDialog title={{ en: "Template writing assistance", zh: "模板编辑帮助" }[context.lang]} closable={false} centerStyle={{
                            width: '48em',
                        }}>
                            <TemplateHelp onClose={() => {
                                this.setState({
                                    templateHelpActive: false
                                });
                            }} />
                        </ModalDialog>
                    )
                }
                {
                    this.state.processorHelpActive && (
                        <ModalDialog title={{ en: "Data pre-processor writing assistance", zh: "数据预处理器编辑帮助" }[context.lang]} closable={false} centerStyle={{
                            width: '48em',
                        }}>
                            <ProcessorHelp onClose={() => {
                                this.setState({
                                    processorHelpActive: false
                                });
                            }} />
                        </ModalDialog>
                    )
                }
                {
                    this.state.downloadActive && (
                        <ModalDialog title={COMMON.TIP_TITLE[context.lang]} closable={false}>
                            <ClientDownload onClose={() => {
                                this.setState({
                                    downloadActive: false
                                });
                            }} />
                        </ModalDialog>
                    )
                }
            </div>
        );
    }
}
